import React, { useCallback, useEffect, useRef, useState } from "react";
import ARProductViewer from "../../components/ar/ARProductViewer";
import PublicARExperience from "../../components/ar/PublicARExperience";
import GatewaySketchPad from "../../components/gateway/GatewaySketchPad";
import useCanonicalArController from "../../core/workspace/ar/useCanonicalArController";
import useToDoARSession, { ACCEPTED_FILES } from "./useToDoARSession";
import { SITE_ORIGIN } from "../../utils/productOrigins";
import "../../pages/GatewayPage.css";
import "../../pages/GatewayMobileFix.css";
import "./ToDoARExperience.css";

const PHOTO_ACCEPT = "image/*";
const MODEL_ACCEPT = ".glb,.gltf,.usdz,.obj,.fbx,.stl,.ply,.dae,.blend,.3dm";
const FILE_ACCEPT = ACCEPTED_FILES;

function DoARLoader({ title = "Creating your DoAR…", detail = "Understanding the source", compact = false }) {
  return (
    <div className={"gateway-loader" + (compact ? " is-compact" : "")} role="status" aria-live="polite">
      <div className="gateway-loader-mark" aria-hidden="true"><i/><i/><i/></div>
      <strong>{title}</strong>
      <span>{detail}</span>
    </div>
  );
}

export default function ToDoARExperience({
  variant = "standalone",
  journeyContext = null,
  active = true,
}) {
  const session = useToDoARSession({ journeyContext });
  const fileRef = useRef(null);
  const fileLabelRef = useRef(null);
  const publicArRef = useRef(null);
  const [addOpen, setAddOpen] = useState(false);
  const [sketchOpen, setSketchOpen] = useState(false);

  const embedded = variant === "embedded";
  const hasResult = Boolean(session.doarId);

  const context = session.journeyContext;
  const contextTitle = String(context?.title || "").trim();
  const ambientContextTitle = String(session.ambientJourneyContext?.title || "").trim();
  const contextCanRemix = context?.rights?.remixAllowed === true;
  const derivedResult = session.continuationMode === "DERIVED_DOAR";
  const placementProfile = session.spatialSession?.spatial?.placementProfile
    || session.product?.placementProfile
    || "floor";
  const setSessionError = session.setError;
  const setSessionStatus = session.setStatus;
  const stopVoice = session.stopVoice;
  const sessionRecording = session.recording;
  const onArFailure = useCallback((error) => {
    setSessionError(error?.message || "AR could not start on this device.");
  }, [setSessionError]);
  const onArExit = useCallback(() => {
    setSessionStatus("");
  }, [setSessionStatus]);
  const ar = useCanonicalArController({
    session: session.spatialSession,
    enabled: active && Boolean(session.spatialSession),
    placementProfile,
    onFailure: onArFailure,
    onExit: onArExit,
  });

  const arRuntimeActive = ar.runtimeActive;
  const stopAr = ar.stop;
  useEffect(() => {
    if (!active && sessionRecording) stopVoice();
    if (!active && arRuntimeActive) stopAr(publicArRef);
  }, [active, arRuntimeActive, sessionRecording, stopAr, stopVoice]);

  const chooseFile = (accept, label) => {
    if (!fileRef.current || session.busy) return;
    fileLabelRef.current = label || null;
    fileRef.current.setAttribute("accept", accept || ACCEPTED_FILES);
    fileRef.current.click();
    setAddOpen(false);
  };

  const onFileChange = event => {
    const file = event.target.files?.[0];
    const label = fileLabelRef.current;
    event.target.value = "";
    fileLabelRef.current = null;
    if (file) session.addFile(file, { label });
  };

  const submitComposer = event => {
    event.preventDefault();
    session.createFromText();
  };

  const openSpatial = () => {
    if (!session.doarId || session.busy || !session.spatialSession) return;
    session.setError("");
    session.setStatus("Opening AR…");
    const started = ar.start(publicArRef);
    if (!started && !ar.preparing) session.setStatus("");
  };

  const submitRefine = event => {
    event.preventDefault();
    session.refine();
  };

  const Root = embedded ? "div" : "main";

  return (
    <Root
      className={
        "gateway-root todoar-experience todoar-experience--" + variant
        + (hasResult ? " gateway-has-result" : "")
      }
      data-todoar-phase={session.phase}
      data-has-result={hasResult ? "true" : "false"}
    >
      {!embedded ? (
        <header className="todoar-header">
          <a className="todoar-brand" href="/" aria-label="ToDoAR home">ToDoAR.co</a>
          <nav className="todoar-header-actions" aria-label="Explore DoAR"><a className="todoar-explore" href={`${SITE_ORIGIN}/`} target="_blank" rel="noopener noreferrer" aria-label="Explore TheDoAR in a new tab">Explore ↗</a></nav>
        </header>
      ) : null}

      <section className="todoar-stage" aria-label="ToDoAR">
        {!hasResult ? (
          <div className="todoar-intake">
            <div className="todoar-intro">
              <p className="todoar-kicker">{embedded ? "ToDoAR.co · Create Reality" : "TO DO AR"}</p>
              {context ? (
                <div className="todoar-context-block">
                  <span className="todoar-context">Using this Reality · {contextTitle}</span>
                  <span className="todoar-context-note">
                    {contextCanRemix
                      ? "The published Reality stays unchanged · edits create a new linked DoAR"
                      : "Reference context only · the published Reality stays unchanged"}
                  </span>
                </div>
              ) : null}
              {session.ambientContextChanged ? (
                <div className="todoar-context-warning">
                  <span>This creation began from {contextTitle}. Switching source starts a new DoAR.</span>
                  <button type="button" onClick={() => session.reset({ preserveAmbientContext: true })}>
                    Start new from {ambientContextTitle || "this Reality"}
                  </button>
                </div>
              ) : null}
              <h1>{context ? `What do you want to do with ${contextTitle}?` : "What do you want to Do AR?"}</h1>
              <p>{context
                ? contextCanRemix
                  ? "Describe the change or outcome you want. DoAR will preserve the source and create a linked derivative when needed."
                  : "Describe the question or outcome you want. The source Reality remains read-only."
                : "Describe it, paste a link, speak, sketch or add a source."}</p>
            </div>

            {sketchOpen ? (
              <div className="todoar-sketch">
                <GatewaySketchPad
                  onCreate={file => {
                    setSketchOpen(false);
                    session.addFile(file, { label: "Your sketch" });
                  }}
                  busy={session.busy}
                />
                <button
                  type="button"
                  className="todoar-sketch-close"
                  onClick={() => setSketchOpen(false)}
                  disabled={session.busy}
                >
                  Back to input
                </button>
              </div>
            ) : (
              <form className={"todoar-composer" + (session.busy ? " is-busy" : "")} onSubmit={submitComposer}>
                <textarea
                  value={session.textValue}
                  onChange={event => session.setTextValue(event.target.value)}
                  placeholder="Describe or paste anything…"
                  maxLength="8000"
                  disabled={session.busy}
                  aria-label="What do you want to Do AR?"
                  rows="3"
                />
                <div className="todoar-composer-actions">
                  <div className="todoar-add">
                    <button
                      type="button"
                      className="todoar-icon-button"
                      aria-expanded={addOpen}
                      aria-label="Add a source"
                      onClick={() => setAddOpen(value => !value)}
                      disabled={session.busy || session.recording}
                    >
                      +
                    </button>
                    {addOpen ? (
                      <div className="todoar-add-menu" role="menu" aria-label="Add source">
                        <button type="button" role="menuitem" onClick={() => chooseFile(PHOTO_ACCEPT, "Your photo")}>Photo</button>
                        <button type="button" role="menuitem" onClick={() => chooseFile(MODEL_ACCEPT, "Your model")}>Model</button>
                        <button type="button" role="menuitem" onClick={() => chooseFile(FILE_ACCEPT, "Your file")}>File</button>
                        <button type="button" role="menuitem" onClick={() => { setSketchOpen(true); setAddOpen(false); }}>Sketch</button>
                      </div>
                    ) : null}
                  </div>

                  <button
                    type="button"
                    className={"todoar-icon-button" + (session.recording ? " is-recording" : "")}
                    onClick={session.toggleVoice}
                    aria-label={session.recording ? "Stop listening" : "Speak to DoAR"}
                    disabled={session.busy && !session.recording}
                  >
                    {session.recording ? "■" : "●"}
                  </button>

                  <button
                    type="submit"
                    className="todoar-submit"
                    disabled={session.busy || session.recording || !session.textValue.trim()}
                  >
                    {session.busy ? "Working…" : "Do AR"}
                  </button>
                </div>

                {session.busy ? (
                  <div className="todoar-forming">
                    <DoARLoader
                      title={String(session.status || "").startsWith("Reading") ? "Understanding the source…" : "Establishing your DoAR…"}
                      detail={session.status || "Understanding your input"}
                    />
                  </div>
                ) : null}
              </form>
            )}

            <input ref={fileRef} hidden type="file" accept={ACCEPTED_FILES} onChange={onFileChange}/>

            <div className="todoar-feedback" aria-live="polite">
              {!session.busy && session.status ? <p className="gateway-status">{session.status}</p> : null}
              {session.error ? <p className="gateway-error" role="alert">{session.error}</p> : null}
            </div>

            <div className="todoar-trust">
              <span>Free to start · No account needed · Private · Temporary</span>
              {!embedded ? <span><a href={`${SITE_ORIGIN}/privacy`}>Privacy</a><a href={`${SITE_ORIGIN}/terms`}>Terms</a></span> : null}
            </div>
          </div>
        ) : (
          <div className="gateway-result todoar-result">
            <div className="gateway-result-head">
              <button type="button" onClick={session.reset} disabled={session.busy}>← New</button>
              <span>{session.resultName}</span>
              <span aria-hidden="true"/>
            </div>
            {context ? (
              <div className="todoar-result-context">
                <span>Source Reality · {contextTitle} · {derivedResult ? "derived copy" : "read-only"}</span>
                {session.ambientContextChanged ? (
                  <span>Open “New” to start from {ambientContextTitle || "the current Reality"}.</span>
                ) : null}
              </div>
            ) : null}

            <div className={"gateway-viewer" + (session.product ? "" : " gateway-viewer-state")}>
              {session.ownerState === "owned" && !session.accountToken ? (
                <div className="gateway-result-state gateway-result-state-recovery">
                  <span className="gateway-result-eyebrow">Saved to your account</span>
                  <strong>{session.resultName}</strong>
                  <p>This temporary browser session has finished. Continue on TheDoAR to use the permanent DoAR.</p>
                  <a className="gateway-result-retry" href={`${SITE_ORIGIN}/login?next=` + encodeURIComponent("/do")}>Continue on TheDoAR</a>
                </div>
              ) : session.ownerState === "expired" ? (
                <div className="gateway-result-state gateway-result-state-recovery">
                  <span className="gateway-result-eyebrow">Temporary session ended</span>
                  <strong>{session.resultName}</strong>
                  <p>This unsaved temporary creation is no longer available.</p>
                  <button type="button" className="gateway-result-retry" onClick={session.reset}>Start a new DoAR</button>
                </div>
              ) : session.product?.glbUrl ? (
                active && !ar.runtimeActive ? (
                  <ARProductViewer
                    product={session.product}
                    arDisabled
                    fallbackMessage="The spatial representation exists, but the interactive preview could not load."
                  />
                ) : <div className="todoar-viewer-suspended" aria-hidden="true" />
              ) : session.product?.usdzUrl ? (
                <div className="gateway-result-state">
                  <span className="gateway-result-eyebrow">Ready for your space</span>
                  <strong>{session.resultName}</strong>
                  <p>Ready to open with Apple Quick Look.</p>
                </div>
              ) : session.busy ? (
                <div className="gateway-result-progress">
                  {session.sourceInfo?.previewUrl ? <img className="gateway-source-preview" src={session.sourceInfo.previewUrl} alt="" /> : null}
                  <DoARLoader compact title={session.resultBusyTitle} detail={session.status || "Working on the next usable state"}/>
                  {session.sourceInfo ? (
                    <div className="gateway-source-summary">
                      <strong>{session.sourceInfo.title}</strong>
                      {session.sourceInfo.merchant || session.sourceInfo.brand ? <span>{[session.sourceInfo.merchant, session.sourceInfo.brand].filter(Boolean).join(" · ")}</span> : null}
                    </div>
                  ) : null}
                </div>
              ) : session.error ? (
                <div className="gateway-result-state gateway-result-state-recovery">
                  {session.sourceInfo?.previewUrl ? <img className="gateway-source-preview" src={session.sourceInfo.previewUrl} alt="" /> : null}
                  <span className="gateway-result-eyebrow">Needs one more reference</span>
                  <strong>{session.resultName}</strong>
                  <p>{session.error}</p>
                  <div className="gateway-recovery-actions">
                    {session.spatialResult ? <button type="button" className="gateway-result-retry" onClick={session.retrySpatial}>Try again</button> : null}
                    <button type="button" className="gateway-result-retry" onClick={() => chooseFile(PHOTO_ACCEPT + ",video/*", "Additional reference")}>Add image or video</button>
                  </div>
                </div>
              ) : session.spatialResult ? (
                <div className="gateway-result-state gateway-result-state-recovery">
                  {session.sourceInfo?.previewUrl ? <img className="gateway-source-preview" src={session.sourceInfo.previewUrl} alt="" /> : null}
                  <span className="gateway-result-eyebrow">Spatial preview not ready</span>
                  <strong>{session.resultName}</strong>
                  <p>{session.status || "The subject is preserved, but no AR-ready representation exists yet."}</p>
                  <div className="gateway-recovery-actions">
                    <button type="button" className="gateway-result-retry" onClick={session.retrySpatial}>Prepare AR again</button>
                    <button type="button" className="gateway-result-retry" onClick={() => chooseFile(ACCEPTED_FILES, "Additional reference")}>Add reference</button>
                  </div>
                </div>
              ) : (
                <div className="gateway-result-state">
                  {session.sourceInfo?.previewUrl ? <img className="gateway-source-preview" src={session.sourceInfo.previewUrl} alt="" /> : null}
                  <span className="gateway-result-eyebrow">{session.sourceInfo ? "Source understood" : "DoAR established"}</span>
                  <strong>{session.resultName}</strong>
                  <p>{session.sourceInfo
                    ? "This source is preserved. If it represents a physical or spatial subject, add a clear image or video and DoAR will continue toward AR."
                    : "Your DoAR identity is established and ready for the next meaningful operation."}</p>
                  <button type="button" className="gateway-result-retry" onClick={() => chooseFile(ACCEPTED_FILES, "Additional reference")}>Add reference</button>
                </div>
              )}

              {session.busy && session.product ? (
                <div className="gateway-viewer-busy">
                  <DoARLoader compact title={session.resultBusyTitle} detail={session.status || "Working on your DoAR"}/>
                </div>
              ) : null}
            </div>

            {session.product ? (
              <button
                className="gateway-ar"
                type="button"
                onClick={openSpatial}
                disabled={session.busy || session.spatialSessionLoading || ar.preparing || !session.spatialSession}
              >
                {session.spatialSessionLoading || ar.preparing
                  ? "Preparing AR…"
                  : session.spatialSession
                    ? "View in my room →"
                    : "AR unavailable"}
              </button>
            ) : null}
            {ar.nativeFallback ? (
              <button type="button" className="gateway-result-retry todoar-native-ar" onClick={() => ar.launchNative(ar.nativeFallback)}>
                Open native AR
              </button>
            ) : null}
            {ar.message ? <p className="gateway-error" role="status">{ar.message}</p> : null}

            {!session.busy && !(session.ownerState === "owned" && !session.accountToken) && session.ownerState !== "expired" ? (
              <div className={"gateway-result-actions" + (!session.spatialResult ? " is-primary-ask" : "")}>
                <button type="button" onClick={() => session.continueTo("ask")}>Ask DoAR</button>
                {session.preview && session.ownerState === "guest" ? <button type="button" onClick={() => session.setRefineOpen(value => !value)}>Adjust</button> : null}
                <button type="button" onClick={() => session.continueTo("edit")} aria-label="Save and edit in Space">{session.spatialResult ? "Edit in Space" : "Open in Space"}</button>
                {session.product ? <button type="button" onClick={() => session.continueTo("publish")}>Publish to TheDoAR</button> : null}
              </div>
            ) : null}

            {session.preview && session.ownerState === "guest" && !session.busy && session.refineOpen ? (
              <form className="gateway-refine" onSubmit={submitRefine}>
                <input
                  value={session.refineText}
                  onChange={event => session.setRefineText(event.target.value)}
                  placeholder="Make it wider…"
                  maxLength="1200"
                  disabled={session.busy}
                />
                <button type="submit" disabled={session.busy || !session.refineText.trim()}>Update</button>
              </form>
            ) : null}

            <input ref={fileRef} hidden type="file" accept={ACCEPTED_FILES} onChange={onFileChange}/>

            {session.error && session.product ? <p className="gateway-error" role="alert">{session.error}</p> : null}
            <PublicARExperience
              ref={publicArRef}
              session={session.spatialSession}
              placementProfile={placementProfile}
              onFailure={ar.handleFailure}
              onExit={ar.handleExit}
            />

            {session.preview ? (
              <p className="gateway-concept-note">
                {session.sourceInfo?.scaleState === "source_supplied"
                  ? "Size comes from the source and remains distinct from user verification."
                  : "Size is not verified yet; AR scale remains explicitly unverified until better evidence is available."}
              </p>
            ) : null}
          </div>
        )}
      </section>
    </Root>
  );
}

export { PHOTO_ACCEPT, MODEL_ACCEPT, FILE_ACCEPT };
